import * as EmailValidator from 'email-validator';
import publicIp from 'public-ip';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getLoginSuccessPagePath } from '../App';
import { getInputLabel } from '../pages/book-link/user-login';
import SwalService, { otpPromptSwal } from '../services/swal';
import { IBook, IUseGetContextResData } from '../types';

/*
|--------------------------------------------------------------------------
| Interfaces
|--------------------------------------------------------------------------
*/

export enum LoginStatuses {
    InProgress,
    Timeout = 'timeout',
    Verified = 'verified',
    Unverified = 'unverified',
    Error = 'error',
    Otp = 'otp',
    Unverifiable = 'isUnverifiable',
}

export type ThandleSubmitLogin = (e: any) => void; // TODO: type event properly

/*
|--------------------------------------------------------------------------
| Hook
|--------------------------------------------------------------------------
*/


export default function useBookLogin(
    isSandbox: boolean,
    cid: string,
    userId: number,
    internalId: string,
    token: string,
    auth: string | null,
    extensionVersion: string | null,
    extensionAuthToken: string | null,
    service: string | null,
    customerName: string | null,
    loginBook: IBook | undefined,
    bookRegionId: string | undefined,
    currentStateAbbr: string | undefined,
    logBookLoginSubmit: () => void,
    logBookLoginResponse: (
        responseStatus: LoginStatuses,
        responseTime: number
    ) => void,
    openIsUnverifiedResponseModal: () => void
): {
    handleSubmitLogin: ThandleSubmitLogin;
    loginStatus: LoginStatuses | null;
    checkLoginStatus: (loadingQueryParam: boolean) => void;
} {
    const [loginStatus, setLoginStatus] = useState<null | LoginStatuses>(null);
    const { push } = useHistory();
    let pollingStartTime: number;

    const delay = async (time: number) => {
        return new Promise(function (resolve) {
            setTimeout(resolve, time);
        });
    };

    let extensionVersionNew = extensionVersion || sessionStorage.getItem('sharpSportsExtensionVersion');

    let isDeepLink =
    ((loginBook?.deepLinkMobile && service) ||
        (loginBook?.deepLinkExtension && extensionAuthToken)) &&
    !isSandbox;

    const TIMEOUT_THRESHOLD = isDeepLink ? 30000 : 2 * 60 * 1000;

    // Handle user submit login credentials
    const handleSubmitLogin: ThandleSubmitLogin = async (e: any) => {
        e.preventDefault();
        const usernameInput = e.currentTarget[0] as any; // TODO: type HTML INPUT ELEMENT PROPERLY properly
        const passwordInput = e.currentTarget[1] as any;
        const username = usernameInput.value;
        const password = passwordInput.value;

        let userAgent = navigator.userAgent;


        // Dont perform validation for DK and PP Extension
        if (!isDeepLink) {
            if (!Boolean(username)) {
                SwalService.bookLoginFormValidation('username');
                return;
            }

            if (!Boolean(password) && String(loginBook?.abbr) != 'fl') {
                SwalService.bookLoginFormValidation('password');
                return;
            }

            if (getInputLabel(loginBook?.abbr || '') == 'Email') {
                if (!EmailValidator.validate(username) && !isSandbox) {
                    SwalService.incorrectBookLoginEmail();
                    return;
                }
            }
        }

        const ip = await publicIp.v4();

        //TODO - validate: should this just be for all deeplink books??
        if (loginBook?.abbr != 'dk' || isSandbox) {
            setLoginStatus(LoginStatuses.InProgress);
        }

        if (loginBook?.abbr === 'dk') {
            let result = await SwalService.DKConsentSwal(customerName);
            if (result.isConfirmed) {
                //pass
            } else {
                return;
            }
        }

        let url = `${process.env.REACT_APP_API_BASE || ''}/v1/bettorAccount`;
        if (auth) {
            url = url.concat(`?auth=${auth}`);
            if (service) url = url.concat(`&service=${service}`);
            if (extensionVersionNew)
                url = url.concat(`&extensionVersion=${extensionVersionNew}`);
        }
        let headers = {
            Authorization: token,
            'Content-Type': 'application/json',
        };
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                username,
                password,
                internalId,
                bookRegion: bookRegionId,
                ip,
                cid: cid,
                userAgent,
            }),
        });

        if (![200, 201].includes(response?.status)) {
            setLoginStatus(LoginStatuses.Error);
            const json = await response.json();
            const detail = json['detail'];
            SwalService.inactiveBookRegion(detail);
            return;
        } else {
            if (isDeepLink && loginBook) {
                if (extensionVersionNew) {
                    const initDeepLinkEvent = new CustomEvent(
                        `init${loginBook.abbr.toUpperCase()}Login`,
                        {
                            detail: {
                                internalId: internalId,
                                book: loginBook.abbr,
                                bookRegionId: bookRegionId,
                                url: window.location.href,
                                cid: cid,
                                userAgent: window.navigator.userAgent,
                            },
                        }
                    );
                    document.dispatchEvent(initDeepLinkEvent);
                }

                //TODO- validate // confirm deepLink mobile always equals extension
                if (loginBook.deepLinkExtension)
                    window.location.href = loginBook.deepLinkExtension.replace('ss-state-abbr',currentStateAbbr || '');
                else if (loginBook.deepLinkMobile)
                    window.location.href = loginBook.deepLinkMobile.replace('ss-state-abbr',currentStateAbbr || '');
            }
            logBookLoginSubmit();
            checkLoginStatus(false);
        }
    };

    const resetLoginStatus = async () => {
        // locally
        setLoginStatus(null);
        // Reset the status context (Q: does this happen independently on the server also?)
        await fetch(
            `${process.env.REACT_APP_API_BASE || ''}/v1/context/?cid=${cid}`,
            {
                method: 'PUT',
                headers: {
                    Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: null,
                    action: null,
                }),
            }
        );
    };

    // Check the server for book login attempt result
    const checkLoginStatus = async (loadingQueryParam: boolean) => {
        if (loadingQueryParam) {
            setLoginStatus(LoginStatuses.InProgress);
        }

        pollingStartTime = pollingStartTime || Number(Date.now());

        //delay polling context
        await delay(2000);

        const res = await fetch(
            `${process.env.REACT_APP_API_BASE || ''}/v1/contexts/${cid}`,
            {
                headers: {
                    Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                },
            }
        );

        const data: IUseGetContextResData = await res.json();
        const status: LoginStatuses | null = data?.status;
        const action = data?.action;
        const otp = data?.otp;

        // if the status belongs to a recognizable Login Status, stop polling and update UI
        if (!!status && Object.values(LoginStatuses).includes(String(status))) {
            // TODO: What happens in the case of InProgress?
            logBookLoginResponse(status, Number(Date.now()) - pollingStartTime);
            switch (status) {
                case LoginStatuses.Verified:
                    if (!!loginBook) {
                        push(
                            getLoginSuccessPagePath(
                                cid,
                                currentStateAbbr || '',
                                bookRegionId || '',
                                loginBook?.name || ''
                            )
                        );
                        resetLoginStatus();
                    }
                    break;

                case LoginStatuses.Error:
                    SwalService.errorResponseSwal(loginBook?.name || '');
                    resetLoginStatus();
                    break;

                case LoginStatuses.Unverified:
                    switch (action) {
                        case 'BADCREDS':
                            SwalService.badCredsResponseSwal();
                            resetLoginStatus();
                            break;

                        case 'BADOTP': //If bad OTP then show 2FA alert and reset OTP to null so we can try again
                            SwalService.bookLoginBadOTPResponseSwal(
                                loginBook?.name || ''
                            );
                            resetLoginStatus();
                            var sendOTPResponse = await fetch(
                                `${
                                    process.env.REACT_APP_API_BASE || ''
                                }/v1/context/?cid=${cid}`,
                                {
                                    method: 'PUT',
                                    headers: {
                                        Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        otp: null,
                                    }),
                                }
                            );
                            break;

                        case '2FA':
                            SwalService.bookLogin2FAResponse(
                                customerName || '',
                                loginBook?.name || ''
                            );
                            resetLoginStatus();
                            break;

                        case 'TOS':
                            SwalService.bookLoginTOSResponse(
                                loginBook?.name || ''
                            );
                            resetLoginStatus();
                            break;

                        case 'LOCATION':
                            SwalService.bookLoginLocationResponse(
                                loginBook?.name || ''
                            );
                            resetLoginStatus();
                            break;

                        default:
                            SwalService.bookLoginActionResponse(
                                loginBook?.name || ''
                            );
                            resetLoginStatus();
                            break;
                    }
                    break;

                case LoginStatuses.Otp:
                    resetLoginStatus();

                    // Fetch OTP from bettor
                    const r = await otpPromptSwal(loginBook?.name || '');
                    const userOTP = r.value;

                    if (userOTP == null || userOTP == '') {
                        // Limit this to three tries?
                        console.error('User cancelled the prompt.');
                        resetLoginStatus();
                        SwalService.bookLoginNoOTPResponseSwal(
                            loginBook?.name || ''
                        );
                    } else {
                        // If user successfully enters OTP, return to InProgress
                        setLoginStatus(LoginStatuses.InProgress);

                        // Send OTP to contex for bot to continue running
                        var sendOTPResponse = await fetch(
                            `${
                                process.env.REACT_APP_API_BASE || ''
                            }/v1/context/?cid=${cid}`,
                            {
                                method: 'PUT',
                                headers: {
                                    Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    status: null,
                                    action: null,
                                    userId: userId, //need this value to get bettorAccount in API
                                    internalId: internalId, //need this value to get bettorAccount in API
                                    bookRegionId: bookRegionId, //need this value to get bettorAccount in API
                                    otp: userOTP,
                                }),
                            }
                        );
                        await delay(8000); //Wait for API to update action,status (should do this better)
                        checkLoginStatus(false);
                    }
                    break;

                case LoginStatuses.Unverifiable:
                    openIsUnverifiedResponseModal();
                    resetLoginStatus();
                    break;
            }
        } else if (Number(Date.now()) - pollingStartTime > TIMEOUT_THRESHOLD) {
            setLoginStatus(null);
            logBookLoginResponse(
                LoginStatuses.Timeout,
                Number(Date.now()) - pollingStartTime
            );
            openIsUnverifiedResponseModal();
        } else {
            checkLoginStatus(false);
        }
    };

    return {
        handleSubmitLogin,
        loginStatus,
        checkLoginStatus,
    };
}
